import { Model, Factory, belongsTo, trait } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import type { SchemaAttributes } from '../../types.js'
import type { MockServer, MirageServer } from '../server.js'
import {
  pageable,
  getExistingEntity,
  createErrorResponse,
  filterable,
} from '../utils.js'
import {
  rand,
  randPastDate,
  randRecentDate,
  randChanceBoolean,
  randAlphaNumeric,
  randNumber,
  randParagraph,
  randCatchPhrase,
} from '@ngneat/falso'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'

export type ConsultationDiagnosisData =
  SchemaAttributes<'ConsultationDiagnosis'>

export const model = (
  Model as ModelDefinition<ConsultationDiagnosisData>
).extend({
  consultation: belongsTo('consultation'),
  patient: belongsTo('patient'),
  createdUser: belongsTo('user'),
  modifiedUser: belongsTo('user'),
  supervisingVeterinarian: belongsTo('user'),
})

export const factory = Factory.extend<ConsultationDiagnosisData>({
  name() {
    return randCatchPhrase()
  },
  code() {
    return randAlphaNumeric({ length: 5 }).join('').toUpperCase()
  },
  diagnosis() {
    return randNumber()
  },
  diagnosis_type() {
    return rand(['DIFFERENTIAL', 'FINAL'])
  },
  category() {
    return rand(['PRIMARY', 'SECONDARY'])
  },
  description() {
    return randChanceBoolean({ chanceTrue: 0.5 }) ? randParagraph() : null
  },
  created() {
    return randPastDate().toISOString()
  },
  modified() {
    return randChanceBoolean({ chanceTrue: 0.2 })
      ? randRecentDate().toISOString()
      : undefined
  },
  is_custom() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
  post_consultation() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
  withCreatedUser: trait({
    afterCreate(diagnosis, server) {
      const createdUser = getExistingEntity(server, 'user')
      diagnosis.update({ createdUser })
    },
  }),
  withModifiedUser: trait({
    afterCreate(diagnosis, server) {
      const modifiedUser = getExistingEntity(server, 'user')
      diagnosis.update({ modifiedUser })
    },
  }),
  withSupervisingVeterinarian: trait({
    afterCreate(diagnosis, server) {
      const supervisingVeterinarian = getExistingEntity(server, 'user')
      diagnosis.update({ supervisingVeterinarian })
    },
  }),
  // withDiagnosis trait is skipped because of the lazy seeding order:
  // first seeds the consultationDiagnosis, then the diagnosis
  afterCreate(diagnosis) {
    if (!diagnosis.modified) {
      diagnosis.update({ modified: diagnosis.created })
    }
  },
})

export function routes(server: MockServer) {
  server.get(
    '/consultations/:parent_lookup_consultation/consultation_diagnoses',
    function (this: MirageServer, schema, request) {
      const consultationId = request.params.parent_lookup_consultation
      const diagnoses = schema.where('consultationDiagnosis', {
        consultationId,
      })

      const sortedDiagnoses = diagnoses.sort(
        (a, b) => Number(b.id) - Number(a.id),
      )

      const { data, meta } = pageable(sortedDiagnoses, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )

  server.post(
    '/consultations/:parent_lookup_consultation/consultation_diagnoses',
    function (this: MirageServer, schema, request) {
      const { data } = JSON.parse(request.requestBody)
      const { attributes } = data

      const diagnosis = schema.find('diagnosis', attributes.diagnosis)

      const previousConsultationDiagnosis = schema.findBy(
        'consultationDiagnosis',
        {
          diagnosis: attributes.diagnosis,
        },
      )

      if (!diagnosis && !previousConsultationDiagnosis) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'Diagnosis is required' }],
        })
      }

      const consultation = schema.find(
        'consultation',
        request.params.parent_lookup_consultation,
      )

      const createdUser = getExistingEntity(server, 'user')

      const consultationDiagnosis = schema.create('consultationDiagnosis', {
        name:
          diagnosis?.attrs.label || previousConsultationDiagnosis?.attrs.name,
        code:
          diagnosis?.attrs.display_code ||
          previousConsultationDiagnosis?.attrs.code,
        diagnosis_type: attributes.diagnosis_type,
        category: attributes.category,
        description: attributes.description || null,
        is_custom: attributes.is_custom || false,
        post_consultation: attributes.post_consultation || false,
        created: getTodayDateTimeISO(),
        createdUser,
        modifiedUser: createdUser,
        supervisingVeterinarian: getExistingEntity(server, 'user'),
        patient: consultation?.patients?.models?.[0],
        consultation,
      })

      return consultationDiagnosis
    },
  )

  // Previous consultation diagnoses
  server.get(
    '/consultations/:parent_lookup_consultation/previous_consultation_diagnoses',
    function (this: MirageServer, schema, request) {
      // Always treat the second consultation as the previous one
      const currentConsultationId = request.params.parent_lookup_consultation
      const secondConsultationId = schema.find('consultation', '2')?.id

      if (currentConsultationId === secondConsultationId) {
        return {
          data: [],
          meta: {
            pagination: { count: 0 },
          },
        }
      }

      const previousDiagnoses = schema.where('consultationDiagnosis', {
        consultationId: secondConsultationId,
      })

      const sorted = previousDiagnoses.sort(
        (a, b) => Number(b.id) - Number(a.id),
      )

      const filtered = filterable(sorted, request, {
        type: (item, value) => item.attrs.diagnosis_type === value,
      })

      const { data, meta } = pageable(filtered, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
          total_count: sorted.length,
          final_count: sorted.filter(
            (item) => item.attrs.diagnosis_type === 'FINAL',
          ).length,
          differential_count: sorted.filter(
            (item) => item.attrs.diagnosis_type === 'DIFFERENTIAL',
          ).length,
        },
      }
    },
  )
}

export function seeds(server: MockServer) {
  const firstConsultation = server.schema.first('consultation')
  if (firstConsultation) {
    server.createList(
      'consultationDiagnosis',
      22,
      'withCreatedUser',
      'withModifiedUser',
      'withSupervisingVeterinarian',
      {
        consultation: firstConsultation,
        patient: firstConsultation?.patients?.models?.[0],
      },
    )
  }

  const secondConsultation = server.schema.find('consultation', '2')
  if (secondConsultation) {
    // Create 2 final diagnoses
    server.createList(
      'consultationDiagnosis',
      2,
      'withCreatedUser',
      'withModifiedUser',
      'withSupervisingVeterinarian',
      {
        consultation: secondConsultation,
        diagnosis_type: 'FINAL',
        patient: secondConsultation?.patients?.models?.[0],
      },
    )
    // Create 2 differential diagnoses
    server.createList(
      'consultationDiagnosis',
      2,
      'withCreatedUser',
      'withModifiedUser',
      'withSupervisingVeterinarian',
      {
        consultation: secondConsultation,
        diagnosis_type: 'DIFFERENTIAL',
        patient: secondConsultation?.patients?.models?.[0],
      },
    )
  }
}
